<template>
  <!-- CD-12 ACCOUNT PAYMENT DETAILS -->
  <div class="space-y-4">
    <div v-if="errorMessage" class="text-red-500 text-[14px]">{{ errorMessage }}</div>

    <div class="w-full md:max-w-[512px] space-y-4">
      <div>
        <LabelComponent for="bank">Bank</LabelComponent>
        <Select v-model="bankDetails.bank" :disabled="!isEditing || isLoading" data-testid="bank-select">
          <SelectTrigger
            :class="[
              inputFocusStyle,
              {
                'border-red-500': errors.bank,
                'border-[#3DD9AB]': !errors.bank && bankDetails.bank && isEditing && !isLoading,
              },
            ]"
          >
            <SelectValue placeholder="Select bank" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="bank in banks" :key="bank._id" :value="bank.name">{{ bank.name }}</SelectItem>
          </SelectContent>
        </Select>
        <p v-if="errors.bank" class="text-red-500 text-sm mt-1">{{ errors.bank }}</p>
        <p class="text-xs text-grey-light-500 mt-2">If your bank is not listed, please contact support.</p>
      </div>

      <div>
        <LabelComponent for="accountType">Account Type</LabelComponent>
        <Select v-model="bankDetails.accountType" :disabled="!isEditing || isLoading" data-testid="account-type-select">
          <SelectTrigger
            :class="[
              inputFocusStyle,
              {
                'border-red-500': errors.accountType,
                'border-[#3DD9AB]': !errors.accountType && bankDetails.accountType && isEditing && !isLoading,
              },
            ]"
          >
            <SelectValue placeholder="Select account type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Savings Account">Savings Account</SelectItem>
            <SelectItem value="Current Account">Current Account</SelectItem>
          </SelectContent>
        </Select>
        <p v-if="errors.accountType" class="text-red-500 text-sm mt-1">{{ errors.accountType }}</p>
      </div>

      <div>
        <LabelComponent for="accountNumber">Bank Account Number</LabelComponent>
        <Input
          id="accountNumber"
          placeholder="Enter account number"
          v-model="bankDetails.accountNumber"
          :disabled="!isEditing || isLoading"
          :class="[
            inputFocusStyle,
            {
              'border-red-500': errors.accountNumber,
              'border-[#3DD9AB]':
                !errors.accountNumber &&
                bankDetails.accountNumber &&
                bankDetails.accountNumber.length >= 6 &&
                bankDetails.accountNumber.length <= 14 &&
                isEditing &&
                !isLoading,
            },
          ]"
          @input="validateAccountNumber"
          @keypress="onlyNumbers"
          data-testid="account-number"
        />
        <p v-if="errors.accountNumber" class="text-red-500 text-sm mt-1">{{ errors.accountNumber }}</p>
      </div>

      <div>
        <LabelComponent for="branchCode">Branch Code</LabelComponent>
        <Input
          id="branchCode"
          placeholder="Branch code"
          v-model="bankDetails.branchCode"
          disabled
          :class="[inputFocusStyle, { 'border-red-500': errors.branchCode }]"
          data-testid="branch-code"
        />
        <p v-if="errors.branchCode" class="text-red-500 text-sm mt-1">{{ errors.branchCode }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Input } from '@/components/ui/input';
import { LabelComponent } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ref, watch } from 'vue';

const inputFocusStyle =
  'border-[#D0D5DD] disabled:bg-grey-light-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus:border-brand-300 focus:shadow-[0_0_0_4px_rgba(15,125,136,0.24)]';

interface Bank {
  _id: string;
  name: string;
  branchCode: string;
}

const props = defineProps<{
  banks: Bank[];
  isEditing: boolean;
  isLoading: boolean;
}>();

const bankDetails = defineModel<{
  bank: string;
  accountType: string;
  accountNumber: string;
  branchCode: string;
}>({ required: true });

const errorMessage = ref('');
const errors = ref({
  bank: '',
  accountType: '',
  accountNumber: '',
  branchCode: '',
});

watch(
  () => bankDetails.value.bank,
  (bankName) => {
    const selectedBank = props.banks.find((bank) => bank.name === bankName);
    if (selectedBank) {
      bankDetails.value.branchCode = selectedBank.branchCode;
    }
  },
);

const validateAccountNumber = () => {
  const accountNumber = bankDetails.value.accountNumber;
  if (accountNumber && (accountNumber.length < 6 || accountNumber.length > 14)) {
    errors.value.accountNumber = 'Account number must be between 6 and 14 digits';
  } else {
    errors.value.accountNumber = '';
  }
};

const onlyNumbers = (event: KeyboardEvent) => {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    event.preventDefault();
  }
};

const handleVerificationResponse = (response: { verified: boolean }) => {
  if (response.verified === false) {
    errorMessage.value = 'The bank details could not be verified. Please check your details and try again.';
  } else {
    errorMessage.value = '';
  }
};

defineExpose({
  validate: () => {
    errors.value.bank = '';
    errors.value.accountType = '';
    errors.value.accountNumber = '';
    errors.value.branchCode = '';

    let isValid = true;

    if (!bankDetails.value.bank) {
      errors.value.bank = 'Bank is required';
      isValid = false;
    }

    if (!bankDetails.value.accountType) {
      errors.value.accountType = 'Account type is required';
      isValid = false;
    }

    if (!bankDetails.value.accountNumber) {
      errors.value.accountNumber = 'Account number is required';
      isValid = false;
    } else if (!/^\d+$/.test(bankDetails.value.accountNumber)) {
      errors.value.accountNumber = 'Account number must contain only numbers';
      isValid = false;
    } else if (bankDetails.value.accountNumber.length < 6 || bankDetails.value.accountNumber.length > 14) {
      errors.value.accountNumber = 'Account number must be between 6 and 14 digits';
      isValid = false;
    }

    if (!bankDetails.value.branchCode) {
      errors.value.branchCode = 'Branch code is required';
      isValid = false;
    }

    return isValid;
  },
  handleVerificationResponse,
});
</script>
